import { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Stack,
  Select,
  Text,
  TextInput,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { notifications } from '@mantine/notifications';
import { MedicationStatement } from '@medplum/fhirtypes';
import { isEqual } from 'lodash';
import { DateInputWrapper } from '@/components/inputs/DateInputWrapper';
import FormActionButtons, { FormActionButtonsRef } from '@/components/FormActionButtons';
import { getMedicationName, setEffectivePeriod, setStatusReason, setStatus, parseEffectiveStart } from '@/utils/medication.utils';
import { STOP_REASONS } from '@consts/medication.consts';

export interface StopMedicationModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (stoppedMedication: MedicationStatement) => void;
  /** Function to stop medication on server. If provided, modal handles async save. */
  onStopToServer?: (
    medication: MedicationStatement,
    endDate: Date,
    reason?: string
  ) => Promise<MedicationStatement>;
  medication?: MedicationStatement;
}

export const StopMedicationModal = ({
  opened,
  onClose,
  onConfirm,
  onStopToServer,
  medication,
}: StopMedicationModalProps) => {
  const { t } = useTranslation(['patient-pages']);

  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const [reasonType, setReasonType] = useState<string | null>(null);
  const [customReason, setCustomReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Refs for FormActionButtons pattern
  const formActionsRef = useRef<FormActionButtonsRef>(null);
  const baselineRef = useRef<{ endDate: Date | null; reasonType: string | null; customReason: string } | null>(null);
  const [baselineSet, setBaselineSet] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (opened) {
      setEndDate(new Date());
      setReasonType(null);
      setCustomReason('');
      setBaselineSet(true); // Flag to capture baseline after state is set
    } else {
      setBaselineSet(false);
    }
  }, [opened]);

  // Capture baseline after form state is initialized
  useEffect(() => {
    if (opened && baselineSet) {
      baselineRef.current = { endDate, reasonType, customReason };
      setBaselineSet(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate, reasonType, customReason]);

  // Change detection for FormActionButtons
  const hasUnsavedChanges = (): boolean => {
    if (!baselineRef.current) {
      return false;
    }
    try {
      return !isEqual(
        { endDate, reasonType, customReason },
        baselineRef.current
      );
    } catch {
      return true;
    }
  };

  // Discard handler for FormActionButtons
  const handleDiscardChanges = () => {
    if (baselineRef.current) {
      setEndDate(baselineRef.current.endDate);
      setReasonType(baselineRef.current.reasonType);
      setCustomReason(baselineRef.current.customReason);
    }
  };

  const handleConfirm = async () => {
    if (!endDate || !medication) {
      return;
    }

    let reason: string | undefined;
    if (reasonType === 'other') {
      reason = customReason || undefined;
    } else if (reasonType) {
      reason = t(`medications.stopReasons.${reasonType}` as never);
    }

    // If onStopToServer is provided, save to server immediately
    if (onStopToServer) {
      setSaving(true);
      try {
        const stoppedMed = await onStopToServer(medication, endDate, reason);
        onConfirm(stoppedMed);
        onClose();
      } catch (error) {
        notifications.show({
          color: 'red',
          title: t('medications.stopModal.saveErrorTitle', {
            defaultValue: 'Failed to stop medication',
          }),
          message: String(error),
        });
      } finally {
        setSaving(false);
      }
    } else {
      // Fallback to local-only confirm - build stopped medication
      let stoppedMed = setStatus(medication, 'stopped');
      stoppedMed = setEffectivePeriod(stoppedMed, parseEffectiveStart(medication), endDate);
      if (reason) {
        stoppedMed = setStatusReason(stoppedMed, reason);
      }
      onConfirm(stoppedMed);
      onClose();
    }
  };

  const medicationName = medication
    ? getMedicationName(medication) || t('medications.unknownMedication')
    : t('medications.unknownMedication');

  const isValid = endDate !== null;

  return (
    <Modal
      opened={opened}
      onClose={() => formActionsRef.current?.handleGuardedClose()}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={t('medications.stopModal.title')}
      size="md"
    >
      <Stack gap="md">
        <Text size="sm">
          {t('medications.stopModal.description', { medication: medicationName })}
        </Text>

        {/* End Date */}
        <DateInputWrapper
          name="stopDate"
          label={t('medications.stopModal.endDateLabel')}
          value={endDate}
          onChange={setEndDate}
          required
        />

        {/* Reason Selection */}
        <Select
          label={t('medications.stopModal.reasonLabel')}
          placeholder={t('medications.stopModal.reasonPlaceholder')}
          data={STOP_REASONS.map((reason) => ({
            value: reason,
            label: t(`medications.stopReasons.${reason}` as never),
          }))}
          value={reasonType}
          onChange={setReasonType}
          clearable
        />

        {/* Custom reason input when "other" is selected */}
        {reasonType === 'other' && (
          <TextInput
            label={t('medications.stopModal.customReasonLabel')}
            placeholder={t('medications.stopModal.customReasonPlaceholder')}
            value={customReason}
            onChange={(e) => setCustomReason(e.currentTarget.value)}
          />
        )}

        {/* Actions */}
        <FormActionButtons
          ref={formActionsRef}
          onSave={handleConfirm}
          onDiscard={handleDiscardChanges}
          onClose={onClose}
          hasUnsavedChanges={hasUnsavedChanges()}
          isLoading={saving}
          saveLabel={t('medications.stopModal.confirmButton')}
          saveButtonProps={{ color: 'red', disabled: !isValid }}
        />
      </Stack>
    </Modal>
  );
};
